
import Dexie, { PromiseExtended, Table } from "dexie";
import Enumerable from "linq";
import _ from "lodash";
import { MemoType } from "../../common/enums/MemoType";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { SystemBalanceTypeEnum } from "../../common/enums/SystemBalanceTypeEnum";
import { IMemoModel } from "../../common/models/IMemoModel";
import { ITagValue } from "../../common/models/ITagValue";
import { ITankStyle } from "../../common/models/TankStyle";
import { keys as runTicketKeys } from "../../queries/run-tickets/Keys";
import { keys as objectHistoryKeys } from "../../queries/object-history/Keys";
import MemosDataService from "../memos-data-service/MemosDataService";
import { INavigationItem } from "../navigation-data-service/Models";
import NavigationDataService from "../navigation-data-service/NavigationDataService";
import { queryClient } from "../query-client-service/QueryClientService";
import { IRunTicketModel } from "../run-tickets-data-service/Models";
import RunTicketsDataService from "../run-tickets-data-service/RunTicketsDataService";
import TagValueDataService from "../tag-value-data-service/TagValueDataService";
import { IDeviceTankPair, IGroupDevicePair, IGroupTankBatteryPair, IMemoAutoText, IOfflineData, IOfflineDevice, IOfflineListText, IOfflinePendingMemo, IOfflinePendingRunTicket, IOfflinePendingTagValue, IOfflinePendingUserActivityLogItem, IOfflineTag, IOfflineTank, IOfflineTankBattery, IOfflineWell, ITankBatteryDevicePair, IWellDevicePair } from "./Models";
import { IUserActivityLogItem } from "../user-activity-log-service/Models";
import UserActivityLogService from "../user-activity-log-service/UserActivityLogService";
import { IFormTemplateApiModel } from "../custom-forms-data-service/TemplateModels";
import { IFormInstanceData } from "../../views/object-form-view/Models";
import CustomFormsDataService from "../custom-forms-data-service/CustomFormsDataService";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import storageService from "../storage/storage-service/StorageService";
import restService, { RestError } from "../rest-service/RestService";
import { publish } from "pubsub-js";
import { OFFLINE_PENDING_CHANGES_SAVED_TOPIC, OfflineChangesMsg } from "../../common/models/pub-sub/PubSubTopics";

class eLynxAppDatabase extends Dexie {
    // Version 1
    groups: Table<INavigationItem[], number> | undefined;
    devices: Table<IOfflineDevice[], number> | undefined;
    groupDeviceMap: Table<IGroupDevicePair[], number> | undefined;
    tags: Table<IOfflineTag[], number> | undefined;
    listText: Table<IOfflineListText[], number> | undefined;
    deviceTankMap: Table<IDeviceTankPair[], number> | undefined;
    memoAutoText: Table<IMemoAutoText[], number> | undefined;
    tankStyles: Table<ITankStyle[], number> | undefined;
    groupTankBatteryMap: Table<IGroupTankBatteryPair[], number> | undefined;
    tankBatteries: Table<IOfflineTankBattery[], number> | undefined;
    tankBatteryDeviceMap: Table<ITankBatteryDevicePair[], number> | undefined;
    pendingTagValues: Table<IOfflinePendingTagValue[], number> | undefined;


    // Version 2
    wells: Table<IOfflineWell[], number> | undefined;
    wellDeviceMap: Table<IWellDevicePair[], number> | undefined;

    // Version 3
    pendingMemos: Table<IOfflinePendingMemo[], number> | undefined;
    pendingRunTickets: Table<IOfflinePendingRunTicket[], number> | undefined;

    // Version 4
    pendingUserActivities: Table<IOfflinePendingUserActivityLogItem[], number> | undefined;

    // Version 5
    formTemplates: Table<IFormTemplateApiModel[], number> | undefined;
    pendingFormInstances: Table<IFormInstanceData[], number> | undefined;

    constructor() {
        super("eLynxApplication");

        //
        // Define tables and indexes
        // (Here's where the implicit table props are dynamically created)
        //


        // use out of line key, primary key will be default of :id
        this.version(1).stores({
            //emails: '++id, contactId, type, email',
            // phones: '++id, contactId, type, phone',
            // contacts: '++id, name',
            groups: '',
            devices: '',
            groupDeviceMap: '',
            tags: '',
            listText: '',
            deviceTankMap: '',
            memoAutoText: '',
            tankStyles: '',
            groupTankBatteryMap: '',
            tankBatteries: '',
            tankBatteryDeviceMap: '',
            pendingTagValues: '',
        });

        this.version(2).stores({
            wellDeviceMap: '',
            wells: '',
        });

        this.version(3).stores({
            pendingMemos: "",
            pendingRunTickets: ""
        });

        this.version(4).stores({
            pendingUserActivity: ""
        });

        this.version(5).stores({
            formTemplates: "",
            pendingFormInstances: ""
        });

        // The following lines are needed for it to work across typescipt using babel-preset-typescript:

        //this.contacts = this.table("contacts");
        this.groups = this.table("groups");
        this.devices = this.table("devices");
        this.groupDeviceMap = this.table("groupDeviceMap");
        this.tags = this.table("tags");
        this.listText = this.table("listText");
        this.deviceTankMap = this.table("deviceTankMap");
        this.memoAutoText = this.table("memoAutoText");
        this.tankStyles = this.table("tankStyles");
        this.groupTankBatteryMap = this.table("groupTankBatteryMap");
        this.tankBatteries = this.table("tankBatteries");
        this.tankBatteryDeviceMap = this.table("tankBatteryDeviceMap");
        this.wells = this.table("wells");
        this.wellDeviceMap = this.table("wellDeviceMap");
        this.pendingTagValues = this.table("pendingTagValues");
        this.pendingMemos = this.table("pendingMemos");
        this.pendingRunTickets = this.table("pendingRunTickets");
        this.pendingUserActivities = this.table("pendingUserActivity");

        this.formTemplates = this.table("formTemplates");
        this.pendingFormInstances = this.table("pendingFormInstances");
    }

    public async populateOfflineData(customerID: number, offlineData: IOfflineData): Promise<void> {

        // clear contents of each table
        this.tables.forEach(async (o) => {
            //await o.clear()
            await o.delete(customerID);
        });

        const promises: PromiseExtended<number>[] = [
            this.groups?.add(offlineData.groups, customerID) as PromiseExtended,
            this.devices?.add(offlineData.devices, customerID) as PromiseExtended,
            this.groupDeviceMap?.add(offlineData.groupDeviceMap, customerID) as PromiseExtended,
            this.tags?.add(offlineData.tags, customerID) as PromiseExtended,
            this.listText?.add(offlineData.listText, customerID) as PromiseExtended,
            this.deviceTankMap?.add(offlineData.deviceTankMap, customerID) as PromiseExtended,
            this.memoAutoText?.add(offlineData.memoAutoText, customerID) as PromiseExtended,
            this.tankStyles?.add(offlineData.tankStyles, customerID) as PromiseExtended,
            this.groupTankBatteryMap?.add(offlineData.groupTankBatteryMap, customerID) as PromiseExtended,
            this.tankBatteries?.add(offlineData.tankBatteries, customerID) as PromiseExtended,
            this.tankBatteryDeviceMap?.add(offlineData.tankBatteryDeviceMap, customerID) as PromiseExtended,
            this.wells?.add(offlineData.wells, customerID) as PromiseExtended,
            this.wellDeviceMap?.add(offlineData.wellDeviceMap, customerID) as PromiseExtended,
            this.formTemplates?.add(offlineData.formTemplates, customerID) as PromiseExtended
        ];

        await Promise.all(promises);
    }
    public async populateOfflineGroups(customerID: number, groups: INavigationItem[]): Promise<void> {

        this.groups?.delete(customerID);
        await this.groups?.add(groups, customerID);
    }
    public async getGroups(customerID: number): Promise<INavigationItem[]> {

        const groups = (await this.groups?.where(":id")
            .equals(customerID)
            .first());

        return groups ?? [];
    }

    public async getGroup(customerID: number, groupID: number): Promise<INavigationItem | undefined> {
        const group = (await this.groups?.where(":id")
            .equals(customerID)
            .first())?.find(o => o.objectID === groupID);

        return group;
    }

    public async getChildGroups(customerID: number, parentID: number): Promise<INavigationItem[]> {

        const groups = (await this.groups?.where(":id")
            .equals(customerID)
            .first());

        return !groups ? [] : Enumerable.from(groups)
            .where(o => o.parentID === parentID)
            .toArray();
    }

    public async getGroupAncestory(customerID: number, groupID: number): Promise<INavigationItem[]> {

        const groups = (await this.groups?.where(":id")
            .equals(customerID)
            .first());

        const group = groups?.find(o => o.objectID === groupID);

        const ancestors: INavigationItem[] = [];

        //https://eslint.org/docs/rules/no-loop-func
        //let parentID = group?.parentID;        
        // while (parentID) {
        //     let parent = groups?.find(o => o.objectID === parentID) as INavigationItem;
        //     if (parent.hasChildren) {
        //         parent.children = await this.getChildGroups(customerID, parent.objectID);
        //     }
        //     ancestors.unshift(parent);
        //     parentID = parent.parentID;
        // }

        for (let parentID = group?.parentID; parentID;) {
            let parent = groups?.find(o => o.objectID === parentID) as INavigationItem;
            if (parent.hasChildren) {
                parent.children = await this.getChildGroups(customerID, parent.objectID);
            }
            ancestors.unshift(parent);
            parentID = parent.parentID;
        }

        return ancestors;
    }

    public async getGroupDevices(customerID: number, groupID: number, includeTanks: boolean, features?: ObjectFeaturesEnum): Promise<IOfflineDevice[]> {

        let data: IOfflineDevice[] = [];

        const groupDeviceMap = (await this.groupDeviceMap?.where(":id")
            .equals(customerID)
            .first())?.filter(o => o.grpID === groupID);

        const devices = (await this.devices?.where(":id")
            .equals(customerID)
            .first());

        if (groupDeviceMap && devices) {

            data = Enumerable.from(devices)
                .where(o => (features === undefined || (o.features & features) > 0))
                .join(groupDeviceMap,
                    o => o.id,
                    v => v.devID,
                    (o, v) => {
                        o.systemBalanceType = v.sbTypeID as SystemBalanceTypeEnum;
                        return o;
                    })
                .toArray();

            if (includeTanks) {
                const deviceTankMap = (await this.deviceTankMap?.where(":id")
                    .equals(customerID)
                    .first());

                if(deviceTankMap) {

                    data = data.concat(Enumerable.from(data)
                            .join(deviceTankMap,
                                o => o.id,
                                v => v.devID,
                                (o, v) => v)
                            .join(devices,
                                o => o.tnkID,
                                v => v.id,
                                (o, v) => v)
                            .distinct(o => o.id)
                        .toArray());
    

                }
            }
        }

        return data;
    }

    public async getGroupTankBatteries(customerID: number, groupID: number, features?: ObjectFeaturesEnum): Promise<IOfflineTankBattery[]> {

        let data: IOfflineTankBattery[] = [];

        const groupMap = (await this.groupTankBatteryMap?.where(":id")
            .equals(customerID)
            .first())?.filter(o => o.grpID === groupID);

        const tankBatteries = (await this.tankBatteries?.where(":id")
            .equals(customerID)
            .first());

        if (groupMap && tankBatteries) {

            data = Enumerable.from(tankBatteries)
                .where(o => (features === undefined || (o.features & features) > 0))
                .join(groupMap,
                    o => o.id,
                    v => v.tnkbID,
                    (o, v) => o)
                .distinct(o => o.id)
                .toArray();
        }

        return data;
    }

    public async getGroupWells(customerID: number, groupID: number, features?: ObjectFeaturesEnum): Promise<IOfflineWell[]> {

        let data: IOfflineWell[] = [];

        const devicesPromise = this.devices?.where(":id")
            .equals(customerID)
            .first();

        const groupMapPromise = this.groupDeviceMap?.where(":id")
            .equals(customerID)
            .first();

        const wellDevicesPromise = this.wellDeviceMap?.where(":id")
            .equals(customerID)
            .first();

        const wellsPromise = this.wells?.where(":id")
            .equals(customerID)
            .first();

        const tagsPromise = this.tags?.where(":id")
            .equals(customerID)
            .first();

        const [devices, groupMap, wellDevices, wells, tags] = await Promise.all([
            devicesPromise,
            groupMapPromise,
            wellDevicesPromise,
            wellsPromise,
            tagsPromise]);


        if (groupMap && wellDevices && wells && devices && tags) {

            Enumerable.from(groupMap.filter(o => o.grpID === groupID))
                .join(devices.filter(o => (features === undefined || (o.features & features) > 0)),
                    o => o.devID,
                    v => v.id,
                    (o, v) => v)
                .join(wellDevices,
                    o => o.id,
                    v => v.devID,
                    (o, v) => v)
                .join(wells,
                    o => o.wellID,
                    v => v.id,
                    (o, v) => v)
                .distinct(o => o.id)
                .forEach((o) => {
                    // ensure only wells with tags that have offline feature, special case for custom forms since tags are not related to the feature
                    if (tags.find(v => v.objID === o.id &&
                        v.objType === eLynxObjectTypeEnum.Well &&
                        (features === undefined || (features & ObjectFeaturesEnum.FormModify) > 0 || (v.features & features) > 0))) {

                        data.push(o);
                    }
                });
        }

        return data;
    }

    public async deleteGroup(customerID: number, groupID: number) : Promise<void> {

        const groups = (await this.groups?.where(":id")
        .equals(customerID)
        .first());

        const newgroups = groups?.filter(o => o.objectID !== groupID) ?? [];

        this.groups?.clear();

        await this.groups?.add(newgroups, customerID);
    }

    public async updateGroup(customerID: number, group: INavigationItem) : Promise<void> {

        const groups = (await this.groups?.where(":id")
        .equals(customerID)
        .first()) ?? [];

        const ndx = groups.findIndex(o => o.objectID === group.objectID) ?? -1;

        if (groups && ndx !== -1) {
            groups[ndx] = group;
        }

        this.groups?.clear();

        await this.groups?.add(groups, customerID);
    }

    public async updateGroups(customerID: number, groups: INavigationItem[]) : Promise<void> {

        const data = (await this.groups?.where(":id")
        .equals(customerID)
        .first()) ?? [];


        groups.forEach(o => {
            const ndx = data.findIndex(v => v.objectID === o.objectID) ?? -1;

            if (data && ndx !== -1) {
                data[ndx] = o;
            }
        });

        this.groups?.clear();

        await this.groups?.add(data, customerID);
    }

        

    


    public async getObject(customerID: number, eLynxObject: IeLynxObject, features?: ObjectFeaturesEnum): Promise<IOfflineDevice | IOfflineTankBattery | IOfflineWell | IOfflineTag | undefined> {

        let data: IOfflineDevice | IOfflineTankBattery | IOfflineWell | IOfflineTag | undefined;

        switch (eLynxObject.objectType) {
            case eLynxObjectTypeEnum.Device:
            case eLynxObjectTypeEnum.Tank:

                const devices = (await this.devices?.where(":id")
                    .equals(customerID)
                    .first());

                if (devices) {
                    data = Enumerable.from(devices)
                        .where(o => (features === undefined || (o.features & features) > 0))
                        .firstOrDefault(o => o.id === eLynxObject.id);
                }

                break;

            case eLynxObjectTypeEnum.Well:
                const wells = (await this.wells?.where(":id")
                    .equals(customerID)
                    ?.first());

                if (wells) {
                    data = Enumerable.from(wells)
                        .firstOrDefault(o => o.id === eLynxObject.id);
                }
                break;

            case eLynxObjectTypeEnum.TankBattery:
                const tankBatteries = (await this.tankBatteries?.where(":id")
                    .equals(customerID)
                    .first());

                if (tankBatteries) {
                    data = Enumerable.from(tankBatteries)
                        .firstOrDefault(o => o.id === eLynxObject.id);
                }

                break;

            case eLynxObjectTypeEnum.Tag:

                const tags = (await this.tags?.where(":id")
                    .equals(customerID)
                    .first());

                if (tags) {
                    data = Enumerable.from(tags)
                        .where(o => (features === undefined || (o.features & features) > 0))
                        .firstOrDefault(o => o.id === eLynxObject.id);
                }

                break;

            default:
                throw new Error(`offline getObject for ${eLynxObject.objectType} is not supported`);
        }


        return data;
    }

    public async updateObject(customerID: number,
                                eLynxObject : IeLynxObject,
                                data: IOfflineDevice | IOfflineTankBattery | IOfflineWell | IOfflineTag): Promise<void> {


        switch (eLynxObject.objectType) {
            case eLynxObjectTypeEnum.Device:
            case eLynxObjectTypeEnum.Tank:


                const devices = (await this.devices?.where(":id")
                    .equals(customerID)
                    .first());

                if (devices) {
                    const ndx = devices.findIndex(o => o.id === eLynxObject.id);
                    devices.splice(ndx, 1, data as IOfflineDevice);
                }

                break;

            case eLynxObjectTypeEnum.Well:

                const wells = (await this.wells?.where(":id")
                    .equals(customerID)
                    ?.first());

                if (wells) {
                    const ndx = wells.findIndex(o => o.id === eLynxObject.id);
                    wells.splice(ndx, 1, data as IOfflineWell);
                }
    
                break;

            case eLynxObjectTypeEnum.TankBattery:
                const tankBatteries = (await this.tankBatteries?.where(":id")
                    .equals(customerID)
                    .first());

                if (tankBatteries) {
                    const ndx = tankBatteries.findIndex(o => o.id === eLynxObject.id);
                    tankBatteries.splice(ndx, 1, data as IOfflineTankBattery);
                }

                break;

            case eLynxObjectTypeEnum.Tag:

                const tags = (await this.tags?.where(":id")
                    .equals(customerID)
                    .first());

                if (tags) {
                    const ndx = tags.findIndex(o => o.id === eLynxObject.id);
                    tags.splice(ndx, 1, data as IOfflineTag);
                }

                break;

            default:
                throw new Error(`offline updateObject for ${eLynxObject.objectType} is not supported`);
        }
    }


    public async getTags(customerID: number, eLynxObject: IeLynxObject, features?: ObjectFeaturesEnum): Promise<IOfflineTag[]> {

        let data: IOfflineTag[] = [];

        const tags = (await this.tags?.where(":id")
            .equals(customerID)
            .first());

        if (tags) {

            data = Enumerable.from(tags)
                .where(o => o.objID === eLynxObject.id &&
                    o.objType === eLynxObject.objectType &&
                    (features === undefined || (o.features & features) > 0))
                .toArray();
        }

        return data;
    }

    public async getDeviceTanks(customerID: number, eLynxObject: IeLynxObject): Promise<IOfflineTank[]> {

        let data: IOfflineTank[] = [];

        const map = (await this.deviceTankMap?.where(":id")
            .equals(customerID)
            .first());

        if (map) {
            const filteredMap = Enumerable.from(map)
                .where(o => o.devID === eLynxObject.id)
                .toArray();


            const tanks = (await this.devices?.where(":id")
                .equals(customerID)
                .first());

            if (tanks) {
                data = Enumerable.from(tanks)
                    .join(filteredMap,
                        o => o.id,
                        v => v.tnkID,
                        (o, v) => o)
                    .toArray();
            }
        }

        return data;
    }

    public async getTankBatteryTanks(customerID: number, eLynxObject: IeLynxObject): Promise<IOfflineTank[]> {

        let data: IOfflineTank[] = [];

        // get the stored tank batteries for the customer
        const tbmap = (await this.tankBatteryDeviceMap?.where(":id")
            .equals(customerID)
            .first());

        // now all device/tank pairs
        const map = (await this.deviceTankMap?.where(":id")
            .equals(customerID)
            .first());

        if (tbmap && map) {

            // filter down to the tanks in tank batteries linked to devices
            const filteredMap = Enumerable.from(tbmap)
                .where(o => o.tnkbID === eLynxObject.id)
                .join(map,
                    o => o.devID,
                    v => v.devID,
                    (o, v) => v.tnkID)
                .toArray();


            const tanks = (await this.devices?.where(":id")
                .equals(customerID)
                .first());

            if (tanks) {
                data = Enumerable.from(tanks)
                    .join(filteredMap,
                        o => o.id,
                        v => v,
                        (o, v) => o)
                    .distinct(o => o.id)
                    .toArray();
            }
        }

        return data;
    }

    public async getWellDevices(customerID: number, eLynxObject: IeLynxObject): Promise<IOfflineDevice[]> {

        let data: IOfflineDevice[] = [];

        // get devices linked to the well
        const map = (await this.wellDeviceMap?.where(":id")
            .equals(customerID)
            .first())?.filter(o => o.wellID === eLynxObject.id);

        if (map) {

            const devices = (await this.devices?.where(":id")
                .equals(customerID)
                .first());

            if (devices) {
                data = Enumerable.from(devices)
                    .join(map,
                        o => o.id,
                        v => v.devID,
                        (o, v) => o)
                    .distinct(o => o.id)
                    .toArray();
            }
        }

        return data;
    }


    public async getLookupLists(customerID: number): Promise<IOfflineListText[]> {

        let data: IOfflineListText[] = [];

        const listText = (await this.listText?.where(":id")
            .equals(customerID)
            .first());

        if (listText) {
            data = listText;
        }

        return data;
    }

    public async getTankStyles(customerID: number): Promise<ITankStyle[]> {

        let data: ITankStyle[] = [];

        const tankStyles = (await this.tankStyles?.where(":id")
            .equals(customerID)
            .first());

        if (tankStyles) {
            data = tankStyles;
        }

        return data;
    }


    public async getPendingTagValues(customerID: number): Promise<IOfflinePendingTagValue[]> {
        let data: IOfflinePendingTagValue[] = [];

        const values = (await this.pendingTagValues?.where(":id")
            .equals(customerID)
            .first());

        if (values) {
            data = values;
        }

        return data;
    }

    public async savePendingTagValues(customerID: number, values: IOfflinePendingTagValue[]): Promise<void> {

        // eliminate duplicates
        const current = await this.getPendingTagValues(customerID);

        values.forEach((newValue) => {

            const matchedItem = Enumerable.from(current)
                .firstOrDefault(currentValue => currentValue.tagID === newValue.tagID &&
                    (currentValue.val === undefined ||
                        (newValue.val !== undefined &&
                            newValue.ts !== undefined &&
                            //(currentValue.ts === undefined || Date.parse(newValue.ts) >= Date.parse(currentValue.ts))
                            (currentValue.ts === undefined || Date.parse(newValue.ts) === Date.parse(currentValue.ts))
                        )));

            // item criteria matched, then update the value and timestamp
            if (matchedItem) {
                matchedItem.txt = newValue.txt;
                matchedItem.val = newValue.val;
                matchedItem.ts = newValue.ts;
            } else {
                current.push(newValue);
            }
        });

        await this.pendingTagValues?.clear();
        await this.pendingTagValues?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    public async deletePendingTagValues(customerID: number, values: IOfflinePendingTagValue[]): Promise<void> {

        const current = await this.getPendingTagValues(customerID);

        values.forEach((value) => {
            _.remove(current, (o) => o.tagID === value.tagID && o.ts === value.ts);
        });

        await this.pendingTagValues?.clear();
        await this.pendingTagValues?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    // Memos
    public async getMemoAutoText(customerID: number, objectType: eLynxObjectTypeEnum): Promise<IMemoAutoText[]> {

        let data: IMemoAutoText[] = [];

        const memoAutoText = (await this.memoAutoText?.where(":id")
            .equals(customerID)
            .first());

        if (memoAutoText) {

            data = memoAutoText.filter(o => o.objectType === objectType);
        }

        return data;
    }

    public async getPendingMemos(customerID: number): Promise<IOfflinePendingMemo[]> {
        let data: IOfflinePendingMemo[] = [];

        const memos = (await this.pendingMemos?.where(":id")
            .equals(customerID)
            .first());

        if (memos) {
            data = memos;
        }

        return data;
    }

    public async savePendingMemos(customerID: number, memos: IOfflinePendingMemo[]): Promise<void> {

        // eliminate duplicates
        const current = await this.getPendingMemos(customerID);

        memos.forEach((newMemo) => {

            const matchedItem = Enumerable
                .from(current)
                .firstOrDefault(currentMemo => currentMemo.id === newMemo.id);

            // item criteria matched, then update the value and timestamp
            if (matchedItem) {
                matchedItem.txt = newMemo.txt;
                matchedItem.ts = newMemo.ts;
            } else {
                // this is a new value so assign it a new id. this emulates
                // an auto incrementing identity column in SQL and allows
                // the UI to treat offline data the same way it treats
                // online data without any special handling.
                newMemo.id = current.length
                    ? Enumerable.from(current).select(o => o.id).max() + 1
                    : 1;
                    
                current.push(newMemo);
            }
        });

        await this.pendingMemos?.clear();
        await this.pendingMemos?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    public async deletePendingMemos(customerID: number, memos: IOfflinePendingMemo[]): Promise<void> {

        const current = await this.getPendingMemos(customerID);

        memos.forEach((memo) => {
            _.remove(current, (o) => o.id === memo.id);
        });

        await this.pendingMemos?.clear();
        await this.pendingMemos?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    // Run Tickets
    public async getPendingRunTickets(customerID: number): Promise<IOfflinePendingRunTicket[]> {
        let data: IOfflinePendingRunTicket[] = [];

        const runTickets = (await this.pendingRunTickets?.where(":id")
            .equals(customerID)
            .first());

        if (runTickets) {
            data = runTickets;
        }

        return data;
    }

    public async savePendingRunTickets(customerID: number, runTickets: IOfflinePendingRunTicket[]): Promise<void> {

        // eliminate duplicates
        const current = await this.getPendingRunTickets(customerID);

        runTickets.forEach((newRunTicket) => {

            const matchedItem = Enumerable
                .from(current)
                .firstOrDefault(currentRunTicket => currentRunTicket.id === newRunTicket.id);

            // item criteria matched, then update applicable properties
            if (matchedItem) {
                matchedItem.tn = newRunTicket.tn;
                matchedItem.pn = newRunTicket.pn;
                matchedItem.cm = newRunTicket.cm;

                matchedItem.opd = newRunTicket.opd;
                matchedItem.tlo = newRunTicket.tlo;
                matchedItem.ilo = newRunTicket.ilo;
                matchedItem.tt = newRunTicket.tt;
                matchedItem.sof = newRunTicket.sof;

                matchedItem.cld = newRunTicket.cld;
                matchedItem.tlc = newRunTicket.tlc;
                matchedItem.ilc = newRunTicket.ilc;
                matchedItem.tb = newRunTicket.tb;
                matchedItem.son = newRunTicket.son;

                matchedItem.sg = newRunTicket.sg;
                matchedItem.bsw = newRunTicket.bsw;

                matchedItem.ats = newRunTicket.ats;
            } else {
                // this is a new value so assign it a new id. this emulates
                // an auto incrementing identity column in SQL and allows
                // the UI to treat offline data the same way it treats
                // online data without any special handling.
                newRunTicket.id = current.length
                    ? Enumerable.from(current).select(o => o.id).max() + 1
                    : 1;
                
                current.push(newRunTicket);
            }
        });

        await this.pendingRunTickets?.clear();
        await this.pendingRunTickets?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.opd))
            .toArray(),
            customerID);
    }

    public async deletePendingRunTickets(customerID: number, runtickets: IOfflinePendingRunTicket[]): Promise<void> {

        const current = await this.getPendingRunTickets(customerID);

        runtickets.forEach((runticket) => {
            _.remove(current, (o) => o.id === runticket.id);
        });

        await this.pendingRunTickets?.clear();
        await this.pendingRunTickets?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.opd))
            .toArray(),
            customerID);
    }

    // User Activity Log
    public async getPendingUserActivities(customerID: number): Promise<IOfflinePendingUserActivityLogItem[]> {
        let data: IOfflinePendingUserActivityLogItem[] = [];

        const userActivities = (await this.pendingUserActivities?.where(":id")
            .equals(customerID)
            .first());

        if (userActivities) {
            data = userActivities;
        }

        return data;
    }

    public async savePendingUserActivities(customerID: number, logItems: IOfflinePendingUserActivityLogItem[]): Promise<void> {

        const current = await this.getPendingUserActivities(customerID);

        logItems.forEach((newLogItem) => {
            // emulate an auto incrementing identity column
            newLogItem.id = current.length
                ? Enumerable.from(current).select(o => o.id).max() + 1
                : 1;

            current.push(newLogItem);
        });

        await this.pendingUserActivities?.clear();
        await this.pendingUserActivities?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    public async deletePendingUserActivities(customerID: number, logItems: IOfflinePendingUserActivityLogItem[]): Promise<void> {

        const current = await this.getPendingUserActivities(customerID);

        logItems.forEach((logItem) => {
            _.remove(current, (o) => o.id === logItem.id);
        });

        await this.pendingUserActivities?.clear();
        await this.pendingUserActivities?.add(Enumerable.from(current)
            .orderBy(o => Date.parse(o.ts))
            .toArray(),
            customerID);
    }

    public async getFormTemplates(customerID: number): Promise<IFormTemplateApiModel[]> {

        const templates = (await this.formTemplates?.where(":id")
            .equals(customerID)
            .first());

        return templates ?? [];
    }

    public async getPendingFormInstances(customerID: number): Promise<IFormInstanceData[]> {
        let data: IFormInstanceData[] = [];

        const instances = (await this.pendingFormInstances?.where(":id")
            .equals(customerID)
            .first());

        if (instances) {
            data = instances;
        }

        return data;
    }

    public async savePendingFormInstances(customerID: number,
        items: IFormInstanceData[]): Promise<void> {

        const current = await this.getPendingFormInstances(customerID);

        items.forEach((o, index) => {

            // since offline, we do not need to keep all of the edits, only the last one entered.
            const lastHistoryItem = Enumerable.from(o.history)
                        .orderByDescending(o => o.modifiedTimestamp.valueOf())
                        .first();
            o.history = [lastHistoryItem];

            // emulate an auto incrementing identity column
            if (o.id === undefined) {
                o.id = (current.length
                ? Enumerable.from(current).select(o => o.id).max() + 1
                : 1).toString();

                current.push(o);
            } else {
                current[index] = o;
            }
        });

        await this.pendingFormInstances?.clear();
        await this.pendingFormInstances?.add(Enumerable.from(current)
            .orderBy(o => o.openedTimestamp.valueOf())
            .toArray(),
            customerID);
    }

    public async deletePendingFormInstances(customerID: number,
        items: IFormInstanceData[]): Promise<void> {

        const current = await this.getPendingFormInstances(customerID);

        items.forEach((o) => {
            _.remove(current, (v) => v.id === o.id);
        });

        await this.pendingFormInstances?.clear();
        await this.pendingFormInstances?.add(Enumerable.from(current)
            .orderBy(o => o.openedTimestamp.valueOf())
            .toArray(),
            customerID);
    }
}

const eLynxDB = new eLynxAppDatabase();

export type OfflineDataOptions = {
    includeTags?: boolean,
    includeTanks?: boolean;
    includeDevices?: boolean;
};

export type OfflineConfigSettings = {
    useLKV: boolean;
    useManualEntryTagCounts: boolean;
    offlineSyncBatchSize: number;
    lastRefresh?: string;
    lastSync?: string;
    enabled?: boolean;
};

const defaultOfflineConfigSettings: OfflineConfigSettings = {
    useLKV: false,
    useManualEntryTagCounts: false,
    offlineSyncBatchSize: 100,
    enabled: true
};


const OfflineConfigKey = 'OfflineConfigSettings';

function setOfflineConfig(settings: OfflineConfigSettings): void {
    storageService.local.set(OfflineConfigKey, settings);
}

function getOfflineConfig(): OfflineConfigSettings {
    return storageService.local.get(OfflineConfigKey, defaultOfflineConfigSettings);
}

async function sendPendingTagValues(customerID: number): Promise<RestError[]> {

    let errors: RestError[] = [];

    // if there are any pending tag values to write, do it now
    const pendingValues = Enumerable.from(await eLynxDB.getPendingTagValues(customerID))
        .orderBy(o => Date.parse(o.ts))
        .toArray();

    if (pendingValues.length) {

        const { offlineSyncBatchSize } = getOfflineConfig();

        let startIndex = 0;
        while (startIndex < pendingValues.length) {

            let pendingSet = Enumerable.from(pendingValues)
                .skip(startIndex)
                .take(offlineSyncBatchSize)
                .toArray();

            let valuesToSave = pendingSet
                .map((o) => {
                    const item: ITagValue = {
                        value: o.val,
                        setPointEventType: SetPointEventTypeEnum.Manual,
                        tagID: o.tagID,
                        unitOfMeasureID: o.uomID,
                        timestamp: new Date(o.ts).toISOString()
                    };
                    return item;
                });

            try {
                // send this batch to the web api
                await TagValueDataService.saveTagValues(customerID, valuesToSave, true, false);

                // remove this set from the local db
                await eLynxDB.deletePendingTagValues(customerID, pendingSet);

            } catch (err) {
                const error = err as RestError;
                if (error && !error.offline) {
                    errors.push(error);
                }
            } finally {
                startIndex += offlineSyncBatchSize;
                const offlineConfig = getOfflineConfig();
                setOfflineConfig({
                    ...offlineConfig,
                    lastSync: new Date().toISOString()
                });
            }
        }
    }

    return errors;
}

async function sendPendingMemos(customerID: number): Promise<RestError[]> {

    let errors: RestError[] = [];

    // if there are any pending memos to write, do it now
    const pendingMemos = Enumerable.from(await eLynxDB.getPendingMemos(customerID))
        .orderBy(o => Date.parse(o.ts))
        .toArray();

    if (pendingMemos.length) {

        const { offlineSyncBatchSize } = getOfflineConfig();

        let startIndex = 0;
        while (startIndex < pendingMemos.length) {

            let pendingSet = Enumerable.from(pendingMemos)
                .skip(startIndex)
                .take(offlineSyncBatchSize)
                .toArray();

            // note: since we stored our data with positive ids in our
            // offline database, we need to set the ids back to -1 before
            // we send the data to our online database so it'll get inserted
            // properly
            let memosToSave = pendingSet
                .map((o) => {
                    const item: IMemoModel = {
                        memoID: -1, //o.id,
                        timestamp: new Date(o.ts),
                        text: o.txt,
                        memoType: o.mt as MemoType,
                        object: {
                            id: o.oid,
                            objectType: o.otid as eLynxObjectTypeEnum
                        },
                        userName: o.un,
                        userID: o.uid
                    };
                    return item;
                });

            try {
                // send this batch to the web api
                await MemosDataService.saveMemos(customerID, memosToSave, false);

                // remove this set from the local db
                await eLynxDB.deletePendingMemos(customerID, pendingSet);

            } catch (err) {
                const error = err as RestError;
                if (error && !error.offline) {
                    errors.push(error);
                }
            } finally {
                startIndex += offlineSyncBatchSize;
                const offlineConfig = getOfflineConfig();
                setOfflineConfig({
                    ...offlineConfig,
                    lastSync: new Date().toISOString()
                });
            }
        }

        queryClient.invalidateQueries(objectHistoryKeys.lists());
    }

    return errors;
}

async function sendPendingRunTickets(customerID: number): Promise<RestError[]> {

    let errors: RestError[] = [];

    // if there are any pending run tickets to write, do it now
    const pendingRunTickets = Enumerable.from(await eLynxDB.getPendingRunTickets(customerID))
        .orderBy(o => Date.parse(o.opd))
        .toArray();

    if (pendingRunTickets.length) {

        const { offlineSyncBatchSize } = getOfflineConfig();

        let startIndex = 0;
        while (startIndex < pendingRunTickets.length) {

            let pendingSet = Enumerable.from(pendingRunTickets)
                .skip(startIndex)
                .take(offlineSyncBatchSize)
                .toArray();

            // note: since we stored our data with positive ids in our
            // offline database, we need to set the ids back to -1 before
            // we send the data to our online database so it'll get inserted
            // properly
            let runticketsToSave = pendingSet
                .map((o) => {
                    const item: IRunTicketModel = {
                        id: -1, // o.id,
                        tank: {
                            id: o.tnk.id,
                            displayText: o.tnk.nm,
                            resourceType: o.tnk.st,
                            tankStyle: {
                                id: o.tnk.ts.id,
                                //inchesMultiplier: o.tnk.ts.im
                                inchesMultiplier: o.tnk.ts.inchesMultiplier,
                                heightInInches: o.tnk.ts.heightInInches,
                                totalBarrels: o.tnk.ts.totalBarrels
                            },
                            isDualPhase: o.tnk.dp
                        },
                        createdDateTime: o.crd,
                        creator: o.cr,
                        ticketNumber: o.tn,
                        purchaserName: o.pn,
                        comments: o.cm,

                        // 1st measurement (open)
                        startDateTime: o.opd,
                        tankLevelOpen: o.tlo,
                        interfaceLevelOpen: o.ilo,
                        temperatureTop: o.tt,
                        sealOff: o.sof,

                        // 2nd measurement (close)
                        completeDateTime: o.cld,
                        tankLevelClose: o.tlc,
                        interfaceLevelClose: o.ilc,
                        temperatureBottom: o.tb,
                        sealOn: o.son,

                        // condition
                        specificGravity: o.sg,
                        basicSedimentsAndWater: o.bsw,

                        // attachments
                        attachments: o.ats.map(att => {
                            return {
                                id: att.id,
                                fileName: att.fn,
                                description: att.des,
                                deleted: att.del,
                                filestoreType: att.ft,
                                lastModified: att.lm,
                                mimeType: att.mt,
                                base64Data: att.dat,
                                url: att.url,
                                thumbnailUrl: att.thu
                            };
                        })
                    };
                    return item;
                });

            try {
                // send this batch to the web api
                await RunTicketsDataService.saveRunTickets(customerID, runticketsToSave, false);

                // remove this set from the local db
                await eLynxDB.deletePendingRunTickets(customerID, pendingSet);

            } catch (err) {
                const error = err as RestError;
                if (error && !error.offline) {
                    errors.push(error);
                }
            } finally {
                startIndex += offlineSyncBatchSize;
                const offlineConfig = getOfflineConfig();
                setOfflineConfig({
                    ...offlineConfig,
                    lastSync: new Date().toISOString()
                });
            }
        }

        queryClient.invalidateQueries(runTicketKeys.lists());
    }

    return errors;
}

async function sendPendingUserActivities(customerID: number): Promise<RestError[]> {

    let errors: RestError[] = [];

    // if there are any pending user activities to write, do it now
    const pendingUserActivities = Enumerable.from(await eLynxDB.getPendingUserActivities(customerID))
        .orderBy(o => Date.parse(o.ts))
        .toArray();

    if (pendingUserActivities.length) {

        const { offlineSyncBatchSize } = getOfflineConfig();

        let startIndex = 0;
        while (startIndex < pendingUserActivities.length) {

            let pendingSet = Enumerable.from(pendingUserActivities)
                .skip(startIndex)
                .take(offlineSyncBatchSize)
                .toArray();

            let userActivitiesToSave = pendingSet
                .map((o) => {
                    const item: IUserActivityLogItem = {
                        clientAccessType: o.cat,
                        activityName: o.an,
                        timestamp: o.ts,
                        clientInfo: o.ci
                    };
                    return item;
                });

            try {
                // send this batch to the web api
                await UserActivityLogService.logUserActivities(customerID, userActivitiesToSave, false);

                // remove this set from the local db
                await eLynxDB.deletePendingUserActivities(customerID, pendingSet);

            } catch (err) {
                const error = err as RestError;
                if (error && !error.offline) {
                    errors.push(error);
                }
            } finally {
                startIndex += offlineSyncBatchSize;
                const offlineConfig = getOfflineConfig();
                setOfflineConfig({
                    ...offlineConfig,
                    lastSync: new Date().toISOString()
                });
            }
        }

        queryClient.invalidateQueries(runTicketKeys.lists());
    }

    return errors;
}

async function sendPendingFormInstances(customerID: number): Promise<RestError[]> {

    let errors: RestError[] = [];

    const pendingInstances = Enumerable.from(await eLynxDB.getPendingFormInstances(customerID))
        .orderBy(o => o.openedTimestamp.valueOf())
        .select(o => {
            return {...o,
                    id: undefined};
            })
        .toArray();

    if (pendingInstances.length) {

        const { offlineSyncBatchSize } = getOfflineConfig();

        let startIndex = 0;
        while (startIndex < pendingInstances.length) {

            let pendingSet = Enumerable.from(pendingInstances)
                .skip(startIndex)
                .take(offlineSyncBatchSize)
                .toArray();
            try {
                // send this batch to the web api
                await CustomFormsDataService.saveInstances(customerID, pendingSet, false);

                // remove this set from the local db
                await eLynxDB.deletePendingFormInstances(customerID, pendingSet);

            } catch (err) {
                const error = err as RestError;
                if (error && !error.offline) {
                    errors.push(error);
                }
            } finally {
                startIndex += offlineSyncBatchSize;
                const offlineConfig = getOfflineConfig();
                setOfflineConfig({
                    ...offlineConfig,
                    lastSync: new Date().toISOString()
                });
            }
        }

        queryClient.invalidateQueries(runTicketKeys.lists());
    }

    return errors;
}


const OfflineDataService = {
    getOfflineConfig: function (): OfflineConfigSettings {
        return getOfflineConfig();
    },
    setOfflineMode: function (enabled: boolean): void {
        const offlineConfig = getOfflineConfig();
        setOfflineConfig({
            ...offlineConfig,
            enabled: enabled
        });
    },
    savePendingChanges: async function (customerID: number, abortSignal?: AbortSignal): Promise<void> {
        await sendPendingFormInstances(customerID);        
        await sendPendingTagValues(customerID);
        await sendPendingMemos(customerID);
        await sendPendingRunTickets(customerID);
        await sendPendingUserActivities(customerID);

        publish(OFFLINE_PENDING_CHANGES_SAVED_TOPIC, {
            customerID: customerID
        } as OfflineChangesMsg);
    },
    getOfflineData: async function (customerID: number, abortSignal?: AbortSignal): Promise<IOfflineData> {

        // await sendPendingFormInstances(customerID);        
        // await sendPendingTagValues(customerID);
        // await sendPendingMemos(customerID);
        // await sendPendingRunTickets(customerID);
        // await sendPendingUserActivities(customerID);

        const offlineData = restService.get<IOfflineData>('/offline/data', abortSignal);
        const groups = NavigationDataService.loadNavigationGroups(abortSignal);
        await Promise.all([offlineData, groups]);

        const response = await offlineData;
        response.data.groups = await groups;

        //console.log(`getOfflineData: ${getTimeframeTimeDisplayText(TimeframeTypeEnum.None, new Date())}`);

        // retries?
        await eLynxDB.populateOfflineData(customerID, response.data);

        const offlineConfig = getOfflineConfig();
        setOfflineConfig({
            ...offlineConfig,
            useLKV: response.data.useLKV,
            useManualEntryTagCounts: response.data.useManualEntryTagCounts,
            offlineSyncBatchSize: response.data.offlineSyncBatchSize,
            lastRefresh: new Date().toISOString()
        });

        return response.data;
    },
    getOfflineGroups: async function (customerID: number, abortSignal?: AbortSignal): Promise<INavigationItem[]> {
        const groups = await NavigationDataService.loadNavigationGroups(abortSignal);
        await eLynxDB.populateOfflineGroups(customerID, groups);
        return groups;
    },
    getGroup: async function (customerID: number, groupID: number): Promise<INavigationItem | undefined> {
        return eLynxDB.getGroup(customerID, groupID);
    },
    getGroupDevices: async function (customerID: number,
        groupID: number,
        includeTanks: boolean,
        features?: ObjectFeaturesEnum): Promise<IOfflineDevice[]> {
        return eLynxDB.getGroupDevices(customerID, groupID, includeTanks, features);
    },
    getGroupTankBatteries: async function (customerID: number,
        groupID: number,
        features?: ObjectFeaturesEnum): Promise<IOfflineTankBattery[]> {
        return eLynxDB.getGroupTankBatteries(customerID, groupID, features);
    },
    getGroupWells: async function (customerID: number,
        groupID: number,
        features?: ObjectFeaturesEnum): Promise<IOfflineWell[]> {
        return eLynxDB.getGroupWells(customerID, groupID, features);
    },
    getChildGroups: async function (customerID: number, parentID: number): Promise<INavigationItem[]> {
        return eLynxDB.getChildGroups(customerID, parentID);
    },
    getGroupAncestory: async function (customerID: number, groupID: number): Promise<INavigationItem[]> {
        return eLynxDB.getGroupAncestory(customerID, groupID);
    },
    getGroups: async function (customerID: number): Promise<INavigationItem[]> {
        return eLynxDB.getGroups(customerID);
    },
    deleteGroup: async function(customerID: number, groupID: number): Promise<void> {
        return eLynxDB.deleteGroup(customerID, groupID);
    },
    updateGroup: async function (customerID: number, group: INavigationItem): Promise<void> {
        return eLynxDB.updateGroup(customerID, group);
    },
    updateGroups: async function(customerID: number, groups: INavigationItem[]): Promise<void> {
        return eLynxDB.updateGroups(customerID, groups);
    },
    getObject: async function (customerID: number,
        eLynxObject: IeLynxObject,
        features?: ObjectFeaturesEnum,
        options?: OfflineDataOptions): Promise<IOfflineDevice | IOfflineTankBattery | IOfflineWell | IOfflineTag | undefined> {

        const offlineObject = await eLynxDB.getObject(customerID, eLynxObject, features);

        if (offlineObject) {
            if (options?.includeTags && eLynxObject.objectType !== eLynxObjectTypeEnum.Tag) {

                const tagObject: IOfflineDevice | IOfflineTankBattery | IOfflineWell = offlineObject;
                tagObject.tags = await eLynxDB.getTags(customerID, eLynxObject, features);
            }

            if (options?.includeTanks) {

                if (eLynxObject.objectType === eLynxObjectTypeEnum.Device) {

                    const offlineDevice = offlineObject as IOfflineDevice;
                    const tanks = await eLynxDB.getDeviceTanks(customerID, eLynxObject);

                    for (let i = 0; i < tanks.length; ++i) {
                        const tank = tanks[i];
                        tank.tags = await eLynxDB.getTags(customerID, {
                            id: tank.id,
                            objectType: eLynxObjectTypeEnum.Tank
                        }, features);
                    }

                    offlineDevice.tanks = tanks;
                } else if (eLynxObject.objectType === eLynxObjectTypeEnum.TankBattery) {
                    const offlineTankBattery = offlineObject as IOfflineTankBattery;
                    const tanks = await eLynxDB.getTankBatteryTanks(customerID, eLynxObject);

                    for (let i = 0; i < tanks.length; ++i) {
                        const tank = tanks[i];
                        tank.tags = await eLynxDB.getTags(customerID, {
                            id: tank.id,
                            objectType: eLynxObjectTypeEnum.Tank
                        }, features);
                    }

                    offlineTankBattery.tanks = tanks;
                }
            } else if (eLynxObject.objectType === eLynxObjectTypeEnum.Well && options?.includeDevices) {
                const offlineWell = offlineObject as IOfflineWell;
                const devices = await eLynxDB.getWellDevices(customerID, eLynxObject);

                for (let i = 0; i < devices.length; ++i) {
                    const dev = devices[i];
                    dev.tags = await eLynxDB.getTags(customerID, {
                        id: dev.id,
                        objectType: eLynxObjectTypeEnum.Device
                    }, features);
                }

                offlineWell.devices = devices;
            }
        }

        return offlineObject;
    },
    getLookupLists: async function (customerID: number): Promise<IOfflineListText[]> {
        return eLynxDB.getLookupLists(customerID);
    },
    getTankStyles: async function (customerID: number): Promise<ITankStyle[]> {
        return eLynxDB.getTankStyles(customerID);
    },
    getPendingTagValues: async function (customerID: number, tagIDs: number[], date?: string): Promise<IOfflinePendingTagValue[]> {
        let pendingValues = await eLynxDB.getPendingTagValues(customerID);
        pendingValues = pendingValues.filter(o => (tagIDs.length === 0 || tagIDs.indexOf(o.tagID) !== -1) && (date === undefined || o.ts === date));
        return pendingValues;
    },
    saveTagValues: async function (customerID: number, values: ITagValue[]): Promise<void> {
        const pendingValues = values.map((o) => {
            const item: IOfflinePendingTagValue = {
                tagID: o.tagID as number,
                txt: o.displayText as string,
                val: o.value as number,
                uomID: o.unitOfMeasureID as number,
                ts: o.timestamp as string
            };
            return item;
        });

        return eLynxDB.savePendingTagValues(customerID, pendingValues);
    },
    getTanks: async function (customerID: number, eLynxObject: IeLynxObject): Promise<IOfflineTank[]> {
        return eLynxDB.getDeviceTanks(customerID, eLynxObject);
    },
    getMemoAutoText: async function (customerID: number, objectType: eLynxObjectTypeEnum): Promise<IMemoAutoText[]> {
        return eLynxDB.getMemoAutoText(customerID, objectType);
    },

    getPendingMemos: async function (customerID: number): Promise<IOfflinePendingMemo[]> {
        let pendingMemos = await eLynxDB.getPendingMemos(customerID);
        return pendingMemos;
    },
    
    saveMemos: async function (customerID: number, memos: IMemoModel[]): Promise<void> {
        const pendingMemos = memos.map((memo) => {
            const item: IOfflinePendingMemo = {
                id: memo.memoID,
                ts: memo.timestamp.toISOString(),
                txt: memo.text,
                mt: memo.memoType,
                oid: memo.object.id,
                otid: memo.object.objectType as number,
                uid: memo.userID ?? 0,
                un: memo.userName ?? ""
            };
            return item;
        });

        return eLynxDB.savePendingMemos(customerID, pendingMemos);
    },
    // getNewMemoID: async function (customerID: number): Promise<number> {
    //     return eLynxDB.getNewMemoID(customerID);
    // },
    deletePendingMemo: async function (customerID: number, memo: IMemoModel) {
        const pendingMemos = await OfflineDataService.getPendingMemos(customerID);
        const pendingMemo = pendingMemos.find(pendingMemo => pendingMemo.id === memo.memoID);

        if (pendingMemo) {
            await eLynxDB.deletePendingMemos(customerID, [pendingMemo]);
        }
    },
    getPendingRunTickets: async function (customerID: number): Promise<IRunTicketModel[]> {
        let pendingRunTickets = await eLynxDB.getPendingRunTickets(customerID);
        return pendingRunTickets.map(runTicket => {

            return {
                id: runTicket.id,
                tank: {
                    id: runTicket.tnk.id,
                    displayText: runTicket.tnk.nm,
                    isDualPhase: runTicket.tnk.dp,
                    resourceType: runTicket.tnk.st,
                    tankStyle: {
                        id: runTicket.tnk.ts.id,
                        inchesMultiplier: runTicket.tnk.ts.inchesMultiplier,
                        totalBarrels: runTicket.tnk.ts.totalBarrels,
                        heightInInches: runTicket.tnk.ts.heightInInches
                        
                    }
                },
                ticketNumber: runTicket.tn,
                createdDateTime: runTicket.crd,
                creator: runTicket.cr,
                purchaserName: runTicket.pn,
                comments: runTicket.cm,

                startDateTime: runTicket.opd,
                tankLevelOpen: runTicket.tlo,
                interfaceLevelOpen: runTicket.ilo,
                temperatureTop: runTicket.tt,
                sealOff: runTicket.sof,

                completeDateTime: runTicket.cld,
                tankLevelClose: runTicket.tlc,
                interfaceLevelClose: runTicket.ilc,
                temperatureBottom: runTicket.tb,
                sealOn: runTicket.son,

                specificGravity: runTicket.sg,
                basicSedimentsAndWater: runTicket.bsw,

                attachments: runTicket.ats.map(att => {
                    return {
                        id: att.id,
                        fileName: att.fn,
                        description: att.des,
                        deleted: att.del,
                        filestoreType: att.ft,
                        lastModified: att.lm,
                        mimeType: att.mt,
                        base64Data: att.dat,
                        url: att.url,
                        thumbnailUrl: att.thu
                    };
                })

                //tankLevelEstimatedHaul: number;
                //interfaceLevelEstimatedHaul: number;
            };

        });
    },
    // getNewRunTicketID: async function (customerID: number): Promise<number> {
    //     return eLynxDB.getNewRunTicketID(customerID);
    // },
    saveRunTickets: async function (customerID: number, runTickets: IRunTicketModel[]): Promise<void> {
        const pendingRunTickets = runTickets.map((runTicket) => {
            const item: IOfflinePendingRunTicket = {
                id: runTicket.id,
                tnk: {
                    id: runTicket.tank.id,
                    nm: runTicket.tank.displayText,
                    st: runTicket.tank.resourceType,
                    ts: {
                        id: runTicket.tank.tankStyle.id,
                        inchesMultiplier: runTicket.tank.tankStyle.inchesMultiplier,
                        totalBarrels: runTicket.tank.tankStyle.totalBarrels,
                        heightInInches: runTicket.tank.tankStyle.heightInInches
                    },
                    dp: runTicket.tank.isDualPhase
                },
                crd: runTicket.createdDateTime,
                cr: runTicket.creator,
                tn: runTicket.ticketNumber,
                pn: runTicket.purchaserName,
                cm: runTicket.comments,

                // 1st measurement (open)
                opd: runTicket.startDateTime,
                tlo: runTicket.tankLevelOpen,
                ilo: runTicket.interfaceLevelOpen,
                tt: runTicket.temperatureTop,
                sof: runTicket.sealOff,

                // 2nd measurement (close)
                cld: runTicket.completeDateTime,
                tlc: runTicket.tankLevelClose,
                ilc: runTicket.interfaceLevelClose,
                tb: runTicket.temperatureBottom,
                son: runTicket.sealOn,

                // condition
                sg: runTicket.specificGravity,
                bsw: runTicket.basicSedimentsAndWater,

                // attachments
                ats: runTicket.attachments.map((at) => {
                    return {
                        id: at.id,
                        fn: at.fileName,
                        des: at.description,
                        del: at.deleted,
                        ft: at.filestoreType,
                        lm: at.lastModified,
                        mt: at.mimeType,
                        dat: at.base64Data,
                        url: at.url,
                        thu: at.thumbnailUrl
                    };
                })
            };
            return item;
        });

        return eLynxDB.savePendingRunTickets(customerID, pendingRunTickets);
    },
    deletePendingRunTicket: async function (customerID: number, runTicket: IRunTicketModel) {
        const pendingRunTickets = await eLynxDB.getPendingRunTickets(customerID);
        const pendingRunTicket = pendingRunTickets.find(pendingRunTicket => pendingRunTicket.id === runTicket.id);

        if (pendingRunTicket) {
            await eLynxDB.deletePendingRunTickets(customerID, [pendingRunTicket]);
        }
    },
    updateObject: async function (customerID: number, 
        eLynxObject : IeLynxObject,
        data: IOfflineDevice | IOfflineTankBattery | IOfflineWell | IOfflineTag): Promise<void> {
            return eLynxDB.updateObject(customerID, eLynxObject, data);
        },
    saveUserActivities: async function (customerID: number, logItems: IUserActivityLogItem[]): Promise<void> {
        const pendingLogItems = logItems.map((logItem) => {
            const item: IOfflinePendingUserActivityLogItem = {
                cat: logItem.clientAccessType,
                an: logItem.activityName,
                ts: logItem.timestamp,
                ci: logItem.clientInfo
            };
            return item;
        });

        return eLynxDB.savePendingUserActivities(customerID, pendingLogItems);
    },
    getFormTemplates: async function (customerID: number, groupID: number, objectType: eLynxObjectTypeEnum): Promise<IFormTemplateApiModel[]> {
        const templates = await eLynxDB.getFormTemplates(customerID);


        if (objectType === eLynxObjectTypeEnum.Group) {

            const p1 = eLynxDB.getGroupDevices(customerID, groupID, true, ObjectFeaturesEnum.FormModify);
            const p2 = OfflineDataService.getGroupWells(customerID, groupID, ObjectFeaturesEnum.FormModify);
            const p3 = OfflineDataService.getGroupTankBatteries(customerID, groupID, ObjectFeaturesEnum.FormModify);

            let [devices, wells, tankBatteries] = await Promise.all([p1, p2, p3]);

            const objectTypes: eLynxObjectTypeEnum[] = [];
            if (devices.length > 0) {
                if (devices.find(o => o.deviceTypeID !== 1000)) {
                    objectTypes.push(eLynxObjectTypeEnum.Device);
                }
                if (devices.find(o => o.deviceTypeID === 1000)) {
                    objectTypes.push(eLynxObjectTypeEnum.Tank);
                }
            }

            if (wells.length > 0) {
                objectTypes.push(eLynxObjectTypeEnum.Well);
            }

            if (tankBatteries.length > 0) {
                objectTypes.push(eLynxObjectTypeEnum.TankBattery);
            }

            const groupTemplates: IFormTemplateApiModel[] = [];
            templates.forEach(o => {

                o.objectTypes.forEach(v => {

                    if (objectTypes.find(x => x === v)) {

                        if (!groupTemplates.find(t => t.id === o.id)) {
                            groupTemplates.push(o);
                        }
                    }
                })
            });

            return groupTemplates;

            // let v = objectTypes.reduce((result: IFormTemplateApiModel[], currentValue) => { 
            //     return result.push(templates.find(o => o.objectTypes.indexOf(currentValue)));
            //     });
    


        } else {
            // return Enumerable.from(templates)
            //     .where(o => o.objectTypes.indexOf(objectType) !== -1)
            //     .toArray();
            return templates.filter(o => o.objectTypes.find(v => v === objectType) !== undefined);
        }
    },
    getPendingFormInstances: async function (customerID: number): Promise<IFormInstanceData[]> {
        return eLynxDB.getPendingFormInstances(customerID);
    },
    savePendingFormInstances: async function (customerID: number, formInstances: IFormInstanceData[]): Promise<void> {
        return eLynxDB.savePendingFormInstances(customerID, formInstances);
    },
    deletePendingFormInstance: async function (customerID: number, formInstnace: IFormInstanceData) {
        const pendingFormInstances = await eLynxDB.getPendingFormInstances(customerID);
        const pendingInstance = pendingFormInstances.find(o => o.id === formInstnace.id);

        if (pendingInstance) {
            await eLynxDB.deletePendingFormInstances(customerID, [pendingInstance]);
        }
    }
};

export default OfflineDataService;
